import type { CanonicalProduct } from '../menu/product.ts';
import { buildOrderLineSnapshot, type ProductSelectionInput } from '../order/product-selection.ts';
import type { ConfirmedComboMatchInput } from '../pricing/explicit-combo.ts';
import { resolveComboMatches, type PricingUnit } from '../pricing/combo.ts';
import type { BusinessPolicySnapshot } from '../policy/business-policy.ts';
import type { LineServiceMode, ServiceMode } from '../contracts/operating-context.ts';
import type {
  AddSmtP01ProductCommand,
  AssignSmtP01ComboSlotCommand,
  AssignSmtP01QuickDrinkCommand,
  ConfirmSmtP01ComboProposalCommand,
  CreateSmtP01ComboGroupCommand,
  OpenSmtP01WorkspaceCommand,
  RemoveSmtP01LineCommand,
  SetSmtP01KeepSingleCommand,
  SetSmtP01LineServiceModeCommand,
  SetSmtP01ModeCommand,
  SetSmtP01ServiceModeCommand,
  SmtP01ComboGroupViewModel,
  SmtP01ComboProposalViewModel,
  SmtP01ComboUnitRef,
  SmtP01DraftLineViewModel,
  SmtP01OrderMode,
  SmtP01PendingRequiredGroupViewModel,
  SmtP01PersistedWorkspaceState,
  SmtP01PoolUnitViewModel,
  SmtP01PricingViewModel,
  SmtP01QuickDrinkTargetViewModel,
  SmtP01WorkspaceDependencies,
  SmtP01WorkspaceFinalizationResult,
  SmtP01WorkspaceMutationContext,
  SmtP01WorkspaceMutationResult,
  SmtP01WorkspaceViewModel,
  UnassignSmtP01ComboSlotCommand,
  UpdateSmtP01LineCommand,
} from './smt-p01-workspace-contract.ts';

export * from './smt-p01-workspace-contract.ts';

type PersistedWorkspaceCore = Omit<SmtP01PersistedWorkspaceState, 'custody'>;
interface DraftLineState { readonly input: ProductSelectionInput }
interface ComboSlotState { readonly slotId: string; readonly selectorKey: string; assignedUnit?: SmtP01ComboUnitRef }
interface ComboGroupState { readonly groupId: string; readonly ruleId: string; readonly slots: ComboSlotState[] }
interface IdempotentResult { readonly signature: string; readonly result: SmtP01WorkspaceMutationResult<unknown> }
interface WorkspaceState {
  readonly workspaceId: string;
  readonly actorId: string;
  revision: number;
  mode: SmtP01OrderMode;
  serviceMode: ServiceMode;
  lineServiceModes: Map<string, ServiceMode>;
  lines: DraftLineState[];
  comboGroups: ComboGroupState[];
  keepSingleUnitKeys: Set<string>;
  readonly idempotency: Map<string, IdempotentResult>;
}

const clone = <T>(value: T): T => structuredClone(value);
const unitKey = (unit: SmtP01ComboUnitRef): string => `${unit.lineId}#${unit.unitIndex}`;
const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, entry]) => [key, stableValue(entry)]),
  );
  return value;
};
const signatureOf = (value: unknown): string => JSON.stringify(stableValue(value));
const assertText = (value: string, code: string): string => {
  if (!value?.trim()) throw new Error(code);
  return value.trim();
};
const assertTimestamp = (value: string): void => {
  if (!Number.isFinite(Date.parse(value))) throw new Error('SMT_WORKSPACE_TIMESTAMP_INVALID');
};
const assertServiceMode = (value: ServiceMode): ServiceMode => {
  if (value !== 'dine-in' && value !== 'takeaway') throw new Error('SMT_WORKSPACE_SERVICE_MODE_INVALID');
  return value;
};
const requiredMinimum = (group: CanonicalProduct['optionGroups'][number]): number =>
  group.required ? Math.max(1, group.min) : group.min;

const normalizeSelections = (selections: readonly { readonly optionGroupId: string; readonly optionIds: readonly string[] }[]) =>
  selections.map((selection) => ({ optionGroupId: selection.optionGroupId, optionIds: [...selection.optionIds] }));

export function createSmtP01WorkspaceAuthority(deps: SmtP01WorkspaceDependencies) {
  const workspaces = new Map<string, WorkspaceState>();
  const quickDrinkSelectorKeys = new Set(deps.quickDrinkSelectorKeys ?? ['drink']);

  const requireWorkspace = (workspaceId: string): WorkspaceState => {
    const state = workspaces.get(assertText(workspaceId, 'SMT_WORKSPACE_ID_REQUIRED'));
    if (!state) throw new Error('SMT_WORKSPACE_NOT_FOUND');
    return state;
  };

  const productRequired = (productId: string): CanonicalProduct => {
    const product = deps.getProduct(productId);
    if (!product) throw new Error('SMT_PRODUCT_NOT_FOUND');
    return product;
  };

  const analyzeLine = (line: DraftLineState): SmtP01DraftLineViewModel => {
    const product = productRequired(line.input.productId);
    const groups = new Map(product.optionGroups.map((group) => [group.id, group]));
    const selected = new Map<string, string[]>();
    for (const selection of line.input.selections) {
      const groupId = assertText(selection.optionGroupId, 'SMT_OPTION_GROUP_ID_REQUIRED');
      const current = selected.get(groupId) ?? [];
      selected.set(groupId, [...current, ...selection.optionIds]);
    }
    for (const [groupId, optionIds] of selected) {
      const group = groups.get(groupId);
      if (!group) throw new Error('SMT_PRODUCT_OPTION_UNKNOWN');
      if (!group.enabled && optionIds.length) throw new Error('SMT_PRODUCT_OPTION_DISABLED');
      if (optionIds.length > group.max) throw new Error('SMT_PRODUCT_OPTION_COUNT_INVALID');
      for (const optionId of optionIds) {
        const option = group.options.find((candidate) => candidate.id === optionId);
        if (!option) throw new Error('SMT_PRODUCT_OPTION_UNKNOWN');
        if (!option.enabled) throw new Error('SMT_PRODUCT_OPTION_DISABLED');
      }
    }
    const pendingRequiredGroups: SmtP01PendingRequiredGroupViewModel[] = [];
    for (const group of [...product.optionGroups].filter((entry) => entry.enabled).sort((a, b) => a.order - b.order)) {
      const minimum = requiredMinimum(group);
      const selectedCount = selected.get(group.id)?.length ?? 0;
      if (selectedCount >= minimum) continue;
      pendingRequiredGroups.push({
        taskId: `${line.input.lineId}:required:${group.id}`,
        lineId: line.input.lineId,
        productId: line.input.productId,
        optionGroupId: group.id,
        label: group.name,
        minimum,
        maximum: group.max,
        selectedCount,
        remainingMinimum: minimum - selectedCount,
        options: [...group.options].sort((a, b) => a.order - b.order)
          .map((option) => ({ optionId: option.id, name: option.name, enabled: option.enabled })),
      });
    }
    return {
      lineId: line.input.lineId,
      productId: line.input.productId,
      operationsName: product.names.operations,
      quantity: line.input.quantity,
      note: line.input.note?.trim() || null,
      selections: normalizeSelections(line.input.selections),
      completion: pendingRequiredGroups.length ? 'pending_required' : 'complete',
      pendingRequiredGroups,
    };
  };

  const lineSnapshots = (state: WorkspaceState) => state.lines.map((line) =>
    buildOrderLineSnapshot(productRequired(line.input.productId), line.input));

  const lineServiceModesFor = (state: WorkspaceState): readonly LineServiceMode[] =>
    [...state.lineServiceModes.entries()]
      .filter(([, serviceMode]) => serviceMode !== state.serviceMode)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([lineId, serviceMode]) => ({ lineId, serviceMode }));

  const completeConfirmedMatches = (state: WorkspaceState): readonly ConfirmedComboMatchInput[] =>
    state.comboGroups
      .filter((group) => group.slots.every((slot) => slot.assignedUnit))
      .map((group) => ({
        ruleId: group.ruleId,
        consumedUnits: group.slots.map((slot) => ({ ...slot.assignedUnit! })),
      }));

  const assignedKeys = (state: WorkspaceState, except?: { groupId: string; slotId: string }): Set<string> => {
    const result = new Set<string>();
    for (const group of state.comboGroups) for (const slot of group.slots) {
      if (except && group.groupId === except.groupId && slot.slotId === except.slotId) continue;
      if (slot.assignedUnit) result.add(unitKey(slot.assignedUnit));
    }
    return result;
  };

  const pricingFor = (state: WorkspaceState, pendingRequiredCount: number): SmtP01PricingViewModel => {
    if (!state.lines.length) return { status: 'empty' };
    if (pendingRequiredCount) return { status: 'pending_required' };
    const policy = deps.getActivePolicy();
    if (!policy) return { status: 'unbound' };
    const quote = deps.quote(lineSnapshots(state), policy, {
      comboMode: 'explicit', confirmedComboMatches: completeConfirmedMatches(state),
    });
    if (quote.kind !== 'quoted') return { status: quote.kind };
    return {
      status: 'quoted', policyVersion: quote.policyVersion,
      pricingRevision: quote.evidence.pricing.pricingRevision,
      currency: quote.evidence.pricing.currency, totalMinor: quote.evidence.pricing.totalMinor,
    };
  };

  const comboGroupsFor = (state: WorkspaceState): readonly SmtP01ComboGroupViewModel[] =>
    state.comboGroups.map((group) => ({
      groupId: group.groupId, ruleId: group.ruleId,
      status: group.slots.every((slot) => slot.assignedUnit) ? 'complete' : 'partial',
      slots: group.slots.map((slot) => ({
        slotId: slot.slotId, selectorKey: slot.selectorKey,
        ...(slot.assignedUnit ? { assignedUnit: { ...slot.assignedUnit } } : {}),
      })),
    }));

  const poolFor = (state: WorkspaceState): readonly SmtP01PoolUnitViewModel[] => {
    const assigned = assignedKeys(state);
    const result: SmtP01PoolUnitViewModel[] = [];
    for (const draft of state.lines) {
      if (analyzeLine(draft).completion !== 'complete') continue;
      const line = buildOrderLineSnapshot(productRequired(draft.input.productId), draft.input);
      for (let unitIndex = 0; unitIndex < line.quantity; unitIndex += 1) {
        const key = unitKey({ lineId: line.lineId, unitIndex });
        if (assigned.has(key)) continue;
        const compatibleTargets = state.comboGroups.flatMap((group) => group.slots
          .filter((slot) => !slot.assignedUnit && line.selectorKeys.includes(slot.selectorKey))
          .map((slot) => ({ groupId: group.groupId, ruleId: group.ruleId, slotId: slot.slotId, selectorKey: slot.selectorKey })));
        result.push({
          lineId: line.lineId, unitIndex, productId: line.productId,
          operationsName: line.productSnapshot.operationsName,
          keepSingle: state.keepSingleUnitKeys.has(key), compatibleTargets,
        });
      }
    }
    return result;
  };

  const quickDrinkTargetsFor = (state: WorkspaceState): readonly SmtP01QuickDrinkTargetViewModel[] => {
    const result: SmtP01QuickDrinkTargetViewModel[] = [];
    for (const group of state.comboGroups) {
      const anchorUnits = group.slots.filter((slot) => slot.assignedUnit).map((slot) => ({ ...slot.assignedUnit! }));
      for (const slot of group.slots) {
        if (slot.assignedUnit || !quickDrinkSelectorKeys.has(slot.selectorKey)) continue;
        const candidates = [...deps.listProductIds()].sort().flatMap((productId) => {
          const product = deps.getProduct(productId);
          const projection = deps.getProductProjection(productId);
          if (!product || !projection?.visible || !product.pricing.selectorKeys.includes(slot.selectorKey)) return [];
          const status = projection.availability === 'blocked' ? 'blocked' as const
            : projection.availability === 'unbound' || projection.basePriceMinor === null ? 'unbound' as const
              : projection.sellable ? 'available' as const : 'blocked' as const;
          return [{
            productId, operationsName: projection.operationsName, status,
            currency: projection.currency, basePriceMinor: projection.basePriceMinor,
            requiresConfiguration: projection.requiresConfiguration,
            optionGroups: projection.optionGroups.map((groupProjection) => ({
              ...groupProjection, options: groupProjection.options.map((option) => ({ ...option })),
            })),
          }];
        });
        result.push({
          targetId: `quick-drink:${group.groupId}:${slot.slotId}`,
          targetLabel: `${group.ruleId}:${slot.selectorKey}`,
          groupId: group.groupId, ruleId: group.ruleId, slotId: slot.slotId,
          selectorKey: slot.selectorKey, anchorUnits, candidates,
        });
      }
    }
    return result;
  };

  const snapshotFor = (state: WorkspaceState): SmtP01WorkspaceViewModel => {
    const lines = state.lines.map(analyzeLine);
    const pendingRequiredTaskCount = lines.reduce((sum, line) => sum + line.pendingRequiredGroups.length, 0);
    const pricing = pricingFor(state, pendingRequiredTaskCount);
    const hasIncompleteStartedCombo = state.comboGroups.some((group) => {
      const assigned = group.slots.filter((slot) => slot.assignedUnit).length;
      return assigned > 0 && assigned < group.slots.length;
    });
    const blockingCodes = [
      ...(pendingRequiredTaskCount ? ['SMT_REQUIRED_CONFIGURATION_PENDING'] : []),
      ...(hasIncompleteStartedCombo ? ['SMT_COMBO_ASSIGNMENT_INCOMPLETE'] : []),
      ...(['blocked', 'rejected', 'unbound'].includes(pricing.status) ? [`SMT_DRAFT_${pricing.status.toUpperCase()}`] : []),
    ];
    const quickTargets = quickDrinkTargetsFor(state);
    return clone({
      workspaceId: state.workspaceId, revision: state.revision, mode: state.mode,
      serviceMode: state.serviceMode, lineServiceModes: lineServiceModesFor(state), lines,
      comboGroups: comboGroupsFor(state), pool: poolFor(state),
      quickDrinks: { targets: quickTargets, remainingRequiredDrinkCount: quickTargets.length },
      pendingRequiredTaskCount, pricing,
      checkout: { blocked: blockingCodes.length > 0, blockingCodes },
    });
  };

  const validateContext = (context: SmtP01WorkspaceMutationContext, state: WorkspaceState): void => {
    assertText(context.actorId, 'SMT_WORKSPACE_ACTOR_REQUIRED');
    assertTimestamp(context.at);
    assertText(context.idempotencyKey, 'SMT_WORKSPACE_IDEMPOTENCY_REQUIRED');
    if (!Number.isSafeInteger(context.expectedRevision) || context.expectedRevision < 0) throw new Error('SMT_WORKSPACE_EXPECTED_REVISION_INVALID');
    if (context.actorId !== state.actorId) throw new Error('SMT_WORKSPACE_ACTOR_MISMATCH');
  };

  const mutate = <T>(context: SmtP01WorkspaceMutationContext, operation: string, command: unknown,
    action: (state: WorkspaceState) => T): SmtP01WorkspaceMutationResult<T> => {
    const state = requireWorkspace(context.workspaceId);
    validateContext(context, state);
    const signature = signatureOf({ operation, context, command });
    const replay = state.idempotency.get(context.idempotencyKey);
    if (replay) return replay.signature === signature
      ? clone(replay.result) as SmtP01WorkspaceMutationResult<T>
      : { kind: 'rejected', code: 'SMT_WORKSPACE_IDEMPOTENCY_CONFLICT', snapshot: snapshotFor(state) };
    if (context.expectedRevision !== state.revision) return { kind: 'rejected', code: 'SMT_WORKSPACE_REVISION_CONFLICT', snapshot: snapshotFor(state) };
    const before = {
      mode: state.mode,
      serviceMode: state.serviceMode,
      lineServiceModes: new Map(state.lineServiceModes),
      lines: clone(state.lines),
      comboGroups: clone(state.comboGroups),
      keepSingle: new Set(state.keepSingleUnitKeys),
    };
    try {
      const value = action(state);
      state.revision += 1;
      const result: SmtP01WorkspaceMutationResult<T> = { kind: 'accepted', snapshot: snapshotFor(state), ...(value === undefined ? {} : { value }) };
      state.idempotency.set(context.idempotencyKey, { signature, result: clone(result) as SmtP01WorkspaceMutationResult<unknown> });
      return clone(result);
    } catch (error) {
      state.mode = before.mode;
      state.serviceMode = before.serviceMode;
      state.lineServiceModes = before.lineServiceModes;
      state.lines = before.lines;
      state.comboGroups = before.comboGroups;
      state.keepSingleUnitKeys = before.keepSingle;
      return { kind: 'rejected', code: error instanceof Error ? error.message : 'SMT_WORKSPACE_MUTATION_REJECTED', snapshot: snapshotFor(state) };
    }
  };

  const activeRule = (ruleId: string) => {
    const rule = deps.getActivePolicy()?.comboRules.find((candidate) => candidate.enabled && candidate.ruleId === ruleId);
    if (!rule) throw new Error('SMT_COMBO_RULE_NOT_FOUND');
    return rule;
  };

  const unitFor = (state: WorkspaceState, ref: SmtP01ComboUnitRef) => {
    if (!Number.isSafeInteger(ref.unitIndex) || ref.unitIndex < 0) throw new Error('SMT_COMBO_UNIT_INVALID');
    const line = lineSnapshots(state).find((candidate) => candidate.lineId === ref.lineId);
    if (!line || ref.unitIndex >= line.quantity) throw new Error('SMT_COMBO_UNIT_NOT_FOUND');
    return { line, key: unitKey(ref) };
  };

  const pricingUnitsFor = (state: WorkspaceState, policy: BusinessPolicySnapshot): readonly PricingUnit[] => {
    const result: PricingUnit[] = [];
    for (const line of lineSnapshots(state)) {
      const baseMinor = policy.pricing.productBaseMinor[line.productId];
      if (typeof baseMinor !== 'number' || !Number.isSafeInteger(baseMinor) || baseMinor < 0) {
        throw new Error('SMT_COMBO_PRODUCT_PRICE_UNBOUND');
      }
      for (let unitIndex = 0; unitIndex < line.quantity; unitIndex += 1) {
        result.push({
          lineId: line.lineId,
          unitIndex,
          productId: line.productId,
          selectorKeys: [...line.selectorKeys],
          baseMinor,
        });
      }
    }
    return result;
  };

  const proposalsFor = (state: WorkspaceState): readonly SmtP01ComboProposalViewModel[] => {
    if (!state.lines.length || state.lines.some((line) => analyzeLine(line).completion !== 'complete')) return [];
    const policy = deps.getActivePolicy();
    const currentPricing = pricingFor(state, 0);
    if (!policy || currentPricing.status !== 'quoted' || currentPricing.totalMinor === undefined) return [];
    const reserved = assignedKeys(state);
    const units = pricingUnitsFor(state, policy).filter((unit) => {
      const key = unitKey(unit); return !reserved.has(key) && !state.keepSingleUnitKeys.has(key);
    });
    const matches = resolveComboMatches(units, policy.comboRules);
    if (!matches.length) return [];
    const groups = matches.map((match) => ({ ruleId: match.ruleId, consumedUnits: match.consumedUnits.map((unit) => ({ ...unit })) }));
    const quote = deps.quote(lineSnapshots(state), policy, {
      comboMode: 'explicit', confirmedComboMatches: [...completeConfirmedMatches(state), ...groups],
    });
    if (quote.kind !== 'quoted') return [];
    const resultingTotalMinor = quote.evidence.pricing.totalMinor;
    const priceDeltaMinor = resultingTotalMinor - currentPricing.totalMinor;
    return [{
      proposalId: `combo-proposal:${state.revision}:${policy.policyVersion}:${policy.pricingRevision}:${signatureOf(groups)}`,
      workspaceRevision: state.revision, policyVersion: policy.policyVersion, pricingRevision: policy.pricingRevision,
      groups, currentTotalMinor: currentPricing.totalMinor, resultingTotalMinor,
      priceDeltaMinor, savingMinor: Math.max(0, -priceDeltaMinor),
    }];
  };

  const makeGroup = (groupId: string, ruleId: string, consumedUnits?: readonly SmtP01ComboUnitRef[]): ComboGroupState => {
    const rule = activeRule(ruleId); const counts = new Map<string, number>(); let cursor = 0;
    const slots = rule.requirements.flatMap((requirement) => Array.from({ length: requirement.quantity }, () => {
      const count = (counts.get(requirement.selectorKey) ?? 0) + 1; counts.set(requirement.selectorKey, count);
      const assignedUnit = consumedUnits?.[cursor++];
      return { slotId: `${groupId}:${requirement.selectorKey}:${count}`, selectorKey: requirement.selectorKey, ...(assignedUnit ? { assignedUnit: { ...assignedUnit } } : {}) };
    }));
    if (consumedUnits && cursor !== consumedUnits.length) throw new Error('SMT_COMBO_PROPOSAL_INVALID');
    return { groupId, ruleId, slots };
  };

  const persistenceFromState = (state: WorkspaceState): PersistedWorkspaceCore => clone({
    workspaceId: state.workspaceId,
    actorId: state.actorId,
    revision: state.revision,
    mode: state.mode,
    serviceMode: state.serviceMode,
    lineServiceModes: lineServiceModesFor(state),
    lines: state.lines.map((line) => clone(line.input)),
    comboGroups: state.comboGroups.map((group) => ({
      groupId: group.groupId,
      ruleId: group.ruleId,
      slots: group.slots.map((slot) => ({
        slotId: slot.slotId,
        selectorKey: slot.selectorKey,
        ...(slot.assignedUnit ? { assignedUnit: clone(slot.assignedUnit) } : {}),
      })),
    })),
    keepSingleUnitKeys: [...state.keepSingleUnitKeys].sort(),
  });

  const stateFromPersistence = (input: PersistedWorkspaceCore): WorkspaceState => {
    const workspaceId = assertText(input.workspaceId, 'SMT_WORKSPACE_ID_REQUIRED');
    const actorId = assertText(input.actorId, 'SMT_WORKSPACE_ACTOR_REQUIRED');
    if (!Number.isSafeInteger(input.revision) || input.revision < 0) throw new Error('SMT_WORKSPACE_PERSISTED_REVISION_INVALID');
    if (input.mode !== 'normal' && input.mode !== 'quick') throw new Error('SMT_WORKSPACE_MODE_INVALID');
    const serviceMode = assertServiceMode(input.serviceMode);
    const lines = input.lines.map((line) => ({ input: clone(line) }));
    const lineIds = new Set(lines.map((line) => line.input.lineId));
    const lineServiceModes = new Map<string, ServiceMode>();
    for (const entry of input.lineServiceModes) {
      const lineId = assertText(entry.lineId, 'SMT_LINE_ID_REQUIRED');
      if (!lineIds.has(lineId)) throw new Error('SMT_LINE_NOT_FOUND');
      if (lineServiceModes.has(lineId)) throw new Error('SMT_LINE_SERVICE_MODE_DUPLICATE');
      const override = assertServiceMode(entry.serviceMode);
      if (override !== serviceMode) lineServiceModes.set(lineId, override);
    }
    const comboGroups = input.comboGroups.map((group) => ({
      groupId: assertText(group.groupId, 'SMT_COMBO_GROUP_ID_REQUIRED'),
      ruleId: assertText(group.ruleId, 'SMT_COMBO_RULE_ID_REQUIRED'),
      slots: group.slots.map((slot) => ({
        slotId: assertText(slot.slotId, 'SMT_COMBO_SLOT_ID_REQUIRED'),
        selectorKey: assertText(slot.selectorKey, 'SMT_COMBO_SELECTOR_REQUIRED'),
        ...(slot.assignedUnit ? { assignedUnit: clone(slot.assignedUnit) } : {}),
      })),
    }));
    return {
      workspaceId,
      actorId,
      revision: input.revision,
      mode: input.mode,
      serviceMode,
      lineServiceModes,
      lines,
      comboGroups,
      keepSingleUnitKeys: new Set(input.keepSingleUnitKeys.map((key) => assertText(key, 'SMT_KEEP_SINGLE_KEY_REQUIRED'))),
      idempotency: new Map(),
    };
  };

  return {
    openWorkspace(command: OpenSmtP01WorkspaceCommand): SmtP01WorkspaceViewModel {
      const workspaceId = assertText(command.workspaceId, 'SMT_WORKSPACE_ID_REQUIRED');
      const actorId = assertText(command.actorId, 'SMT_WORKSPACE_ACTOR_REQUIRED'); assertTimestamp(command.at);
      const mode = command.initialMode ?? 'normal'; if (!['normal', 'quick'].includes(mode)) throw new Error('SMT_WORKSPACE_MODE_INVALID');
      const serviceMode = assertServiceMode(command.initialServiceMode ?? 'takeaway');
      const existing = workspaces.get(workspaceId);
      if (existing) { if (existing.actorId !== actorId) throw new Error('SMT_WORKSPACE_ACTOR_MISMATCH'); return snapshotFor(existing); }
      const state: WorkspaceState = {
        workspaceId, actorId, revision: 0, mode, serviceMode, lineServiceModes: new Map(),
        lines: [], comboGroups: [], keepSingleUnitKeys: new Set(), idempotency: new Map(),
      };
      workspaces.set(workspaceId, state); return snapshotFor(state);
    },
    snapshot: (workspaceId: string) => snapshotFor(requireWorkspace(workspaceId)),
    comboProposals: (workspaceId: string) => clone(proposalsFor(requireWorkspace(workspaceId))),
    prepareFinalization(workspaceId: string, expectedRevision: number): SmtP01WorkspaceFinalizationResult {
      const state = requireWorkspace(workspaceId); const snapshot = snapshotFor(state);
      if (!Number.isSafeInteger(expectedRevision) || expectedRevision !== state.revision) return { kind: 'rejected', code: 'SMT_WORKSPACE_REVISION_CONFLICT', snapshot };
      if (snapshot.checkout.blocked) return { kind: 'rejected', code: snapshot.checkout.blockingCodes[0] ?? 'SMT_WORKSPACE_CHECKOUT_BLOCKED', snapshot };
      return {
        kind: 'accepted', workspaceId, revision: state.revision,
        serviceMode: state.serviceMode, lineServiceModes: lineServiceModesFor(state),
        lines: state.lines.map((line) => clone(line.input)), confirmedComboMatches: completeConfirmedMatches(state).map(clone),
      };
    },
    hasWorkspace: (workspaceId: string): boolean => workspaces.has(assertText(workspaceId, 'SMT_WORKSPACE_ID_REQUIRED')),
    exportPersistenceState: (workspaceId: string): PersistedWorkspaceCore => persistenceFromState(requireWorkspace(workspaceId)),
    projectPersistenceState: (input: PersistedWorkspaceCore): SmtP01WorkspaceViewModel => snapshotFor(stateFromPersistence(input)),
    restorePersistenceState(input: PersistedWorkspaceCore): SmtP01WorkspaceViewModel {
      if (workspaces.has(input.workspaceId)) throw new Error('SMT_WORKSPACE_ID_CONFLICT');
      const state = stateFromPersistence(input);
      workspaces.set(state.workspaceId, state);
      return snapshotFor(state);
    },
    removeWorkspace(workspaceId: string): void {
      const accepted = assertText(workspaceId, 'SMT_WORKSPACE_ID_REQUIRED');
      if (!workspaces.delete(accepted)) throw new Error('SMT_WORKSPACE_NOT_FOUND');
    },
    setMode: (context: SmtP01WorkspaceMutationContext, command: SetSmtP01ModeCommand) => mutate(context, 'setMode', command, (state) => {
      if (!['normal', 'quick'].includes(command.mode)) throw new Error('SMT_WORKSPACE_MODE_INVALID'); state.mode = command.mode;
    }),
    setServiceMode: (context: SmtP01WorkspaceMutationContext, command: SetSmtP01ServiceModeCommand) => mutate(context, 'setServiceMode', command, (state) => {
      state.serviceMode = assertServiceMode(command.serviceMode);
      for (const [lineId, serviceMode] of state.lineServiceModes) if (serviceMode === state.serviceMode) state.lineServiceModes.delete(lineId);
    }),
    setLineServiceMode: (context: SmtP01WorkspaceMutationContext, command: SetSmtP01LineServiceModeCommand) => mutate(context, 'setLineServiceMode', command, (state) => {
      const lineId = assertText(command.lineId, 'SMT_LINE_ID_REQUIRED');
      if (!state.lines.some((line) => line.input.lineId === lineId)) throw new Error('SMT_LINE_NOT_FOUND');
      const serviceMode = assertServiceMode(command.serviceMode);
      if (serviceMode === state.serviceMode) state.lineServiceModes.delete(lineId);
      else state.lineServiceModes.set(lineId, serviceMode);
    }),
    addProduct: (context: SmtP01WorkspaceMutationContext, command: AddSmtP01ProductCommand) => mutate(context, 'addProduct', command, (state) => {
      if (!Number.isSafeInteger(command.quantity) || command.quantity <= 0) throw new Error('SMT_LINE_QUANTITY_INVALID');
      const input: ProductSelectionInput = { lineId: assertText(command.lineId, 'SMT_LINE_ID_REQUIRED'), productId: assertText(command.productId, 'SMT_PRODUCT_ID_REQUIRED'), quantity: command.quantity, selections: normalizeSelections(command.selections), note: command.note?.trim() || null };
      if (state.lines.some((line) => line.input.lineId === input.lineId)) throw new Error('SMT_LINE_ID_CONFLICT');
      if (!deps.getProductProjection(input.productId)?.sellable) throw new Error('SMT_PRODUCT_NOT_SELLABLE');
      const draft = { input }; const analyzed = analyzeLine(draft);
      if (analyzed.completion === 'pending_required' && state.mode !== 'quick') throw new Error('SMT_REQUIRED_CONFIGURATION_PENDING');
      state.lines = [...state.lines, draft];
      if (analyzed.completion === 'complete' && pricingFor(state, 0).status !== 'quoted') throw new Error(`SMT_DRAFT_${pricingFor(state, 0).status.toUpperCase()}`);
    }),
    updateLine: (context: SmtP01WorkspaceMutationContext, command: UpdateSmtP01LineCommand) => mutate(context, 'updateLine', command, (state) => {
      if (!Number.isSafeInteger(command.quantity) || command.quantity <= 0) throw new Error('SMT_LINE_QUANTITY_INVALID');
      const index = state.lines.findIndex((line) => line.input.lineId === assertText(command.lineId, 'SMT_LINE_ID_REQUIRED'));
      if (index < 0) throw new Error('SMT_LINE_NOT_FOUND');
      const current = state.lines[index].input;
      const updated = { input: { ...current, quantity: command.quantity, selections: normalizeSelections(command.selections), note: command.note?.trim() || null } };
      const analyzed = analyzeLine(updated); if (analyzed.completion === 'pending_required' && state.mode !== 'quick') throw new Error('SMT_REQUIRED_CONFIGURATION_PENDING');
      state.lines = state.lines.map((line, i) => i === index ? updated : line);
      if (state.lines.every((line) => analyzeLine(line).completion === 'complete') && pricingFor(state, 0).status !== 'quoted') throw new Error(`SMT_DRAFT_${pricingFor(state, 0).status.toUpperCase()}`);
    }),
    removeLine: (context: SmtP01WorkspaceMutationContext, command: RemoveSmtP01LineCommand) => mutate(context, 'removeLine', command, (state) => {
      const lineId = assertText(command.lineId, 'SMT_LINE_ID_REQUIRED');
      if (!state.lines.some((line) => line.input.lineId === lineId)) throw new Error('SMT_LINE_NOT_FOUND');
      state.lines = state.lines.filter((line) => line.input.lineId !== lineId);
      state.lineServiceModes.delete(lineId);
      for (const key of [...state.keepSingleUnitKeys]) if (key.startsWith(`${lineId}#`)) state.keepSingleUnitKeys.delete(key);
      for (const group of state.comboGroups) for (const slot of group.slots) {
        if (slot.assignedUnit?.lineId === lineId) delete slot.assignedUnit;
      }
      if (
        state.lines.length > 0
        && state.lines.every((line) => analyzeLine(line).completion === 'complete')
        && pricingFor(state, 0).status !== 'quoted'
      ) throw new Error(`SMT_DRAFT_${pricingFor(state, 0).status.toUpperCase()}`);
    }),
    setKeepSingle: (context: SmtP01WorkspaceMutationContext, command: SetSmtP01KeepSingleCommand) => mutate(context, 'setKeepSingle', command, (state) => {
      const ref = { lineId: assertText(command.lineId, 'SMT_LINE_ID_REQUIRED'), unitIndex: command.unitIndex }; const { key } = unitFor(state, ref);
      if (assignedKeys(state).has(key)) throw new Error('SMT_COMBO_UNIT_ALREADY_ASSIGNED'); command.keepSingle ? state.keepSingleUnitKeys.add(key) : state.keepSingleUnitKeys.delete(key);
    }),
    createComboGroup: (context: SmtP01WorkspaceMutationContext, command: CreateSmtP01ComboGroupCommand) => mutate(context, 'createComboGroup', command, (state) => {
      const groupId = assertText(command.groupId, 'SMT_COMBO_GROUP_ID_REQUIRED'); const ruleId = assertText(command.ruleId, 'SMT_COMBO_RULE_ID_REQUIRED');
      if (state.comboGroups.some((group) => group.groupId === groupId)) throw new Error('SMT_COMBO_GROUP_ID_CONFLICT'); state.comboGroups = [...state.comboGroups, makeGroup(groupId, ruleId)];
    }),
    assignComboSlot: (context: SmtP01WorkspaceMutationContext, command: AssignSmtP01ComboSlotCommand) => mutate(context, 'assignComboSlot', command, (state) => {
      const group = state.comboGroups.find((entry) => entry.groupId === command.groupId); if (!group) throw new Error('SMT_COMBO_GROUP_NOT_FOUND');
      const slot = group.slots.find((entry) => entry.slotId === command.slotId); if (!slot) throw new Error('SMT_COMBO_SLOT_NOT_FOUND');
      const ref = { lineId: assertText(command.lineId, 'SMT_LINE_ID_REQUIRED'), unitIndex: command.unitIndex }; const { line, key } = unitFor(state, ref);
      if (state.keepSingleUnitKeys.has(key)) throw new Error('SMT_COMBO_UNIT_KEEP_SINGLE'); if (!line.selectorKeys.includes(slot.selectorKey)) throw new Error('SMT_COMBO_UNIT_INCOMPATIBLE');
      if (assignedKeys(state, { groupId: group.groupId, slotId: slot.slotId }).has(key)) throw new Error('SMT_COMBO_UNIT_ALREADY_ASSIGNED'); slot.assignedUnit = ref;
      if (!state.lines.some((entry) => analyzeLine(entry).completion !== 'complete') && pricingFor(state, 0).status !== 'quoted') throw new Error(`SMT_DRAFT_${pricingFor(state, 0).status.toUpperCase()}`);
    }),
    unassignComboSlot: (context: SmtP01WorkspaceMutationContext, command: UnassignSmtP01ComboSlotCommand) => mutate(context, 'unassignComboSlot', command, (state) => {
      const group = state.comboGroups.find((entry) => entry.groupId === command.groupId); if (!group) throw new Error('SMT_COMBO_GROUP_NOT_FOUND');
      const slot = group.slots.find((entry) => entry.slotId === command.slotId); if (!slot) throw new Error('SMT_COMBO_SLOT_NOT_FOUND'); delete slot.assignedUnit;
    }),
    confirmComboProposal: (context: SmtP01WorkspaceMutationContext, command: ConfirmSmtP01ComboProposalCommand) => mutate(context, 'confirmComboProposal', command, (state) => {
      const proposal = proposalsFor(state).find((entry) => entry.proposalId === assertText(command.proposalId, 'SMT_COMBO_PROPOSAL_ID_REQUIRED'));
      if (!proposal) throw new Error('SMT_COMBO_PROPOSAL_STALE');
      state.comboGroups = [...state.comboGroups, ...proposal.groups.map((group, index) => makeGroup(`auto:${state.revision}:${index + 1}:${group.ruleId}`, group.ruleId, group.consumedUnits))];
      if (pricingFor(state, 0).status !== 'quoted') throw new Error(`SMT_DRAFT_${pricingFor(state, 0).status.toUpperCase()}`);
    }),
    assignQuickDrink: (context: SmtP01WorkspaceMutationContext, command: AssignSmtP01QuickDrinkCommand) => mutate(context, 'assignQuickDrink', command, (state) => {
      const target = quickDrinkTargetsFor(state).find((entry) => entry.targetId === assertText(command.targetId, 'SMT_QUICK_DRINK_TARGET_ID_REQUIRED'));
      if (!target) throw new Error('SMT_QUICK_DRINK_TARGET_NOT_FOUND');
      const candidate = target.candidates.find((entry) => entry.productId === command.productId); if (!candidate) throw new Error('SMT_QUICK_DRINK_CANDIDATE_NOT_COMPATIBLE');
      if (candidate.status !== 'available') throw new Error('SMT_QUICK_DRINK_CANDIDATE_BLOCKED');
      const group = state.comboGroups.find((entry) => entry.groupId === target.groupId); const slot = group?.slots.find((entry) => entry.slotId === target.slotId);
      if (!slot || slot.assignedUnit) throw new Error('SMT_QUICK_DRINK_TARGET_NOT_FOUND');
      let ref: SmtP01ComboUnitRef;
      if (command.existingLineId) {
        const line = state.lines.find((entry) => entry.input.lineId === command.existingLineId && entry.input.productId === command.productId);
        if (!line) throw new Error('SMT_QUICK_DRINK_LINE_MISMATCH'); if (analyzeLine(line).completion !== 'complete') throw new Error('SMT_QUICK_DRINK_CONFIGURATION_REQUIRED');
        ref = { lineId: command.existingLineId, unitIndex: command.unitIndex ?? 0 };
      } else {
        const lineId = assertText(command.newLineId ?? '', 'SMT_QUICK_DRINK_NEW_LINE_ID_REQUIRED'); if (state.lines.some((entry) => entry.input.lineId === lineId)) throw new Error('SMT_LINE_ID_CONFLICT');
        const draft = { input: { lineId, productId: command.productId, quantity: 1, selections: normalizeSelections(command.selections), note: command.note?.trim() || null } };
        if (analyzeLine(draft).completion !== 'complete') throw new Error('SMT_QUICK_DRINK_CONFIGURATION_REQUIRED'); state.lines = [...state.lines, draft]; ref = { lineId, unitIndex: 0 };
      }
      const { line, key } = unitFor(state, ref); if (!line.selectorKeys.includes(target.selectorKey)) throw new Error('SMT_QUICK_DRINK_CANDIDATE_NOT_COMPATIBLE');
      if (state.keepSingleUnitKeys.has(key)) throw new Error('SMT_COMBO_UNIT_KEEP_SINGLE'); if (assignedKeys(state).has(key)) throw new Error('SMT_COMBO_UNIT_ALREADY_ASSIGNED');
      slot.assignedUnit = ref; if (state.lines.some((entry) => analyzeLine(entry).completion !== 'complete')) throw new Error('SMT_QUICK_DRINK_CONFIGURATION_REQUIRED');
      if (pricingFor(state, 0).status !== 'quoted') throw new Error(`SMT_DRAFT_${pricingFor(state, 0).status.toUpperCase()}`);
    }),
  };
}
